import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

import Lottie from 'lottie-react';

import { LinearProgress, Typography, Box } from '@mui/material';

import { mobileAudioManager, setupMobileAudioUnlock } from '../../../shared/utils/mobile-audio-utils';
import { webAudioSfx } from '../../../shared/utils/web-audio';

import bombSvg from './Assets/img/bomb.svg';
import flagSvg from './Assets/img/flag.svg';
import settingsSvg from './Assets/img/settings.svg';
import smileSvg from './Assets/img/smile.svg';
import giftAnim from './Assets/lottie/gift-lottie.json';
import looseSound from './Assets/sounds/loose.mp3';
import mouseClickSound from './Assets/sounds/mouseClick.mp3';
import titanikSound from './Assets/sounds/titanik.mp3';
import winSound from './Assets/sounds/win.mp3';
// @ts-ignore


import './miner-header.css';

// @ts-ignore
// import catAnim from '../../../assets/lottie/catAnim.json'; // Отключено для быстрой загрузки Минера

interface MinerGameProps {
  gameKey: any;
  gameSettings?: any;
  difficulty: 'easy' | 'medium' | 'hard';
  onChangeDifficulty?: (newDifficulty: 'easy' | 'medium' | 'hard') => void;
  showIntroModal: boolean;
  isPlaying: boolean;
  handleGiftClick: () => void;
  handleLottieComplete: () => void;
  lottieRef: React.RefObject<any>;
  modalOpacity: number;
  gameOpacity: number;
}

interface Cell {
  isMine: boolean;
  isRevealed: boolean;
  isFlagged: boolean;
  neighborMines: number;
}

const HEADER_HEIGHT = 'clamp(48px, 10vh, 120px)';
const PANEL_HEIGHT = 'clamp(48px, 9vh, 90px)';

// Новый компонент для одной игровой сессии
const MinerGameInstance = (props: MinerGameProps) => {
  // Режим тестирования
  const isTesting = false;
  
  // Инициализируем MobileAudioManager и звуки эффектов
  React.useEffect(() => {
    setupMobileAudioUnlock();
    
    // Инициализируем звуки эффектов
    const initSfx = async () => {
      try {
        // Предзагружаем звуки в webAudioSfx
        if (webAudioSfx.isReady()) {
          await webAudioSfx.preload('click', mouseClickSound, 0.3);
          await webAudioSfx.preload('win', winSound, 0.5);
          await webAudioSfx.preload('loose', looseSound, 0.5);
          await webAudioSfx.preload('titanik', titanikSound, 0.4);
          setSfxReady(true);
          console.log('✅ Звуки эффектов загружены в webAudioSfx');
        } else {
          // Фолбек на MobileAudioManager
          await mobileAudioManager.preloadAudio('click', { src: mouseClickSound, volume: 0.3 });
          await mobileAudioManager.preloadAudio('win', { src: winSound, volume: 0.5 });
          await mobileAudioManager.preloadAudio('loose', { src: looseSound, volume: 0.5 });
          await mobileAudioManager.preloadAudio('titanik', { src: titanikSound, volume: 0.4 });
          setSfxReady(true);
          console.log('✅ Звуки эффектов загружены в mobileAudioManager');
        }
      } catch (error) {
        console.warn('⚠️ Ошибка при загрузке звуков эффектов:', error);
        // Устанавливаем готовность даже при ошибке, чтобы игра не блокировалась
        setSfxReady(true);
      }
    };
    
    initSfx();
    
    // Добавляем глобальные обработчики для разблокировки аудио
    const handleGlobalInteraction = () => {
      console.log('🎵 Глобальное взаимодействие пользователя обнаружено');
      setUserInteracted(true);
      // Уведомляем MobileAudioManager о взаимодействии
      mobileAudioManager.setUserInteracted();
      // Устанавливаем audioReady (музыка всегда доступна через URL)
      setAudioReady(true);
      console.log('✅ audioReady установлен в true');
    };
    
    // Добавляем обработчики на различные события
    const events = ['touchstart', 'click', 'keydown', 'mousedown'];
    events.forEach(event => {
      document.addEventListener(event, handleGlobalInteraction, { once: true, passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleGlobalInteraction);
      });
    };
  }, []);
  
  // Аудио элемент для музыки
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [titleFontSize, setTitleFontSize] = useState(24);
  const [titleClickCount, setTitleClickCount] = useState(0);
  const [titleScaleAnimation, setTitleScaleAnimation] = useState(false);
  
  // Удалить внутренний стейт difficulty
  // const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [fieldOffset, setFieldOffset] = useState({ x: 0, y: 0 });
  const [cellSize, setCellSize] = useState(40);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [gameOverMessage, setGameOverMessage] = useState('');
  const [isWin, setIsWin] = useState(false);
  const fieldRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  // Аудио рефы для всех звуков
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Состояние для отслеживания готовности аудио
  const [audioReady, setAudioReady] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [sfxReady, setSfxReady] = useState(false);
  
  // 1. difficulties и currentDifficulty всегда вычисляются на каждый рендер
  const difficulties = {
    easy: { width: 11, height: 15, mines: 12 },
    medium: { width: 16, height: 20, mines: 35 },
    hard: { width: 20, height: 24, mines: 60 }
  };
  const currentDifficulty = difficulties[props.difficulty];

  // 1. Добавить стейт gameInstanceKey
  const [gameInstanceKey, setGameInstanceKey] = useState(0);

  // 2. initializeField всегда использует актуальный currentDifficulty
  const initializeField = React.useCallback(() => {
    const field: Cell[][] = [];
    for (let y = 0; y < currentDifficulty.height; y++) {
      field[y] = [];
      for (let x = 0; x < currentDifficulty.width; x++) {
        field[y][x] = {
          isMine: false,
          isRevealed: false,
          isFlagged: false,
          neighborMines: 0
        };
      }
    }
    return field;
  }, [currentDifficulty.height, currentDifficulty.width]);

  const [gameState, setGameState] = useState({
    time: 0,
    mines: currentDifficulty.mines,
    flags: 0,
    gameStarted: false,
    gameEnded: false,
    won: false,
    gameMode: 'reveal' as 'reveal' | 'flag',
    field: initializeField()
  });

  // Добавляем стейты для загрузки ассетов
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [assetsProgress, setAssetsProgress] = useState(0); // 0-100
  const [preloadedImages, setPreloadedImages] = useState<{start?: string; end?: string}>({});
  
  // Получаем URL музыки напрямую (как в Capybara), без предзагрузки
  const backgroundMusic = props.gameSettings?.musicUrl || titanikSound;

  // Предзагрузка только изображений (музыку загружаем напрямую, как в Capybara)
  React.useEffect(() => {
    let isMounted = true;
    const urls = [
      props.gameSettings?.startGameImageUrl,
      props.gameSettings?.endGameImageUrl
    ].filter(Boolean);
    
    if (urls.length === 0) {
      setAssetsLoaded(true);
      setAssetsProgress(100);
      return;
    }
    
    let loaded = 0;
    const total = urls.length;
    const images: {start?: string; end?: string} = {};

    const updateProgress = () => {
      loaded++;
      if (isMounted) setAssetsProgress(Math.round((loaded / total) * 100));
      if (loaded === total && isMounted) {
        setPreloadedImages(images);
        setAssetsLoaded(true);
      }
    };

    // Картинки
    if (props.gameSettings?.startGameImageUrl) {
      const img = new window.Image();
      img.onload = () => { images.start = props.gameSettings.startGameImageUrl; updateProgress(); };
      img.onerror = () => updateProgress();
      img.src = props.gameSettings.startGameImageUrl;
    }
    if (props.gameSettings?.endGameImageUrl) {
      const img = new window.Image();
      img.onload = () => { images.end = props.gameSettings.endGameImageUrl; updateProgress(); };
      img.onerror = () => updateProgress();
      img.src = props.gameSettings.endGameImageUrl;
    }
    
    return () => { isMounted = false; };
  }, [props.gameSettings]);

  // Функция для подбора размера шрифта заголовка
  const calculateTitleFontSize = (text: string, containerHeight: number, containerWidth: number, maxHeaderHeight: number) => {
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.visibility = 'hidden';
    testElement.style.fontWeight = 'bold';
    testElement.style.fontFamily = 'inherit';
    testElement.style.wordWrap = 'break-word';
    testElement.style.maxWidth = `${containerWidth - 30}px`;
    testElement.style.lineHeight = '1.1';
    testElement.style.padding = '0';
    testElement.style.margin = '0';
    testElement.style.border = 'none';
    testElement.textContent = text;
    document.body.appendChild(testElement);
    let fontSize = 120;
    let fits = false;
    while (fontSize > 10 && !fits) {
      testElement.style.fontSize = `${fontSize + 5}px`;
      testElement.offsetHeight;
      const rect = testElement.getBoundingClientRect();
      const actualHeight = rect.height;
      const actualWidth = rect.width;
      if (actualHeight <= maxHeaderHeight && actualWidth <= containerWidth - 20) {
        fits = true;
      } else {
        fontSize -= 1;
      }
    }
    document.body.removeChild(testElement);
    return Math.max(fontSize, 10);
  };

  // Размещение мин
  const placeMines = (field: Cell[][], firstX: number, firstY: number) => {
    let minesPlaced = 0;
    const newField = [...field];
    const minePositions: { x: number, y: number }[] = [];

    while (minesPlaced < currentDifficulty.mines) {
      const x = Math.floor(Math.random() * currentDifficulty.width);
      const y = Math.floor(Math.random() * currentDifficulty.height);
      
      // Не ставим мину на первый клик и вокруг него
      if (!newField[y][x].isMine && 
          (Math.abs(x - firstX) > 1 || Math.abs(y - firstY) > 1)) {
        newField[y][x].isMine = true;
        minePositions.push({ x, y });
        minesPlaced++;
      }
    }
    




    // Подсчитываем количество мин вокруг каждой клетки
    for (let y = 0; y < currentDifficulty.height; y++) {
      for (let x = 0; x < currentDifficulty.width; x++) {
        if (!newField[y][x].isMine) {
          let count = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const ny = y + dy;
              const nx = x + dx;
              if (ny >= 0 && ny < currentDifficulty.height && 
                  nx >= 0 && nx < currentDifficulty.width) {
                if (newField[ny][nx].isMine) {
                  count++;
                }
              }
            }
          }
          newField[y][x].neighborMines = count;
        }
      }
    }

    return newField;
  };

  // Рекурсивное открытие пустых клеток
  const revealEmptyCells = (x: number, y: number, field: Cell[][]) => {
    const newField = [...field];
    const queue: { x: number, y: number }[] = [];
    const visited = new Set<string>();
    
    // Добавляем соседние клетки начальной клетки в очередь
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        
        const ny = y + dy;
        const nx = x + dx;
        const key = `${nx},${ny}`;
        
        if (
          ny >= 0 && ny < currentDifficulty.height && 
          nx >= 0 && nx < currentDifficulty.width &&
          newField[ny] && newField[ny][nx] && // добавлена проверка
          !newField[ny][nx].isRevealed && !newField[ny][nx].isFlagged &&
          !visited.has(key)
        ) {
          queue.push({ x: nx, y: ny });
          visited.add(key);
        }
      }
    }
    
    let index = 0;
    while (index < queue.length) {
      const { x: cx, y: cy } = queue[index++];
      const key = `${cx},${cy}`;
      
      if (!newField[cy] || !newField[cy][cx]) continue; // добавлена проверка
      if (visited.has(key) && newField[cy][cx].isRevealed) {
        continue;
      }
      
      newField[cy][cx].isRevealed = true;
      visited.add(key);
      
      // Если клетка пустая, добавляем соседние в очередь
      if (newField[cy][cx].neighborMines === 0) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            
            const ny = cy + dy;
            const nx = cx + dx;
            const neighborKey = `${nx},${ny}`;
            
            if (
              ny >= 0 && ny < currentDifficulty.height && 
              nx >= 0 && nx < currentDifficulty.width &&
              newField[ny] && newField[ny][nx] && // добавлена проверка
              !newField[ny][nx].isRevealed && !newField[ny][nx].isFlagged &&
              !visited.has(neighborKey)
            ) {
              queue.push({ x: nx, y: ny });
              visited.add(neighborKey);
            }
          }
        }
      }
    }
    
    return newField;
  };

  // Открытие клетки
  const revealCell = React.useCallback((x: number, y: number) => {
    // Проверяем, что поле существует и инициализировано
    if (!gameState.field || !gameState.field[y] || !gameState.field[y][x]) {
      return;
    }

    if (gameState.field[y][x].isRevealed || gameState.field[y][x].isFlagged) return;

    let newField = [...gameState.field];
    newField[y][x].isRevealed = true;

    if (newField[y][x].isMine) {
      // Игрок попал на мину
      // Воспроизводим звук проигрыша
      playLooseSound();
      
      // НЕ останавливаем музыку при проигрыше - она должна продолжать играть
      console.log('Музыка продолжает играть при проигрыше');
      
      setGameState(prev => ({ 
        ...prev, 
        gameEnded: true, 
        won: false, 
        field: newField 
      }));
      setGameOverMessage(props.gameSettings?.loseText || '');
      setIsWin(false);
      setShowGameOverModal(true);
      return;
    }

    // Если клетка пустая, рекурсивно открываем соседние
    if (newField[y][x].neighborMines === 0) {
      newField = revealEmptyCells(x, y, newField);
    }

    // Проверяем победу сразу
    let safeCellsRevealed = 0;
    const totalSafeCells = currentDifficulty.width * currentDifficulty.height - currentDifficulty.mines;
    
    for (let y = 0; y < currentDifficulty.height; y++) {
      for (let x = 0; x < currentDifficulty.width; x++) {
        if (!newField[y][x].isMine && newField[y][x].isRevealed) {
          safeCellsRevealed++;
        }
      }
    }
    
    const isWin = safeCellsRevealed === totalSafeCells;
    
    setGameState(prev => ({ 
      ...prev, 
      field: newField,
      gameEnded: isWin,
      won: isWin
    }));
    
    if (isWin) {
      // Воспроизводим звук победы
      playWinSound();
      
      setGameOverMessage(props.gameSettings?.winText || 'Ты победил!');
      setIsWin(true);
      setShowGameOverModal(true);
    }
  }, [gameState.field, props.gameSettings?.winText, props.gameSettings?.loseText, currentDifficulty.width, currentDifficulty.height, currentDifficulty.mines, revealEmptyCells]);



  // Установка/снятие флажка
  const toggleFlag = React.useCallback((x: number, y: number) => {
    // Проверяем, что поле существует и инициализировано
    if (!gameState.field || !gameState.field[y] || !gameState.field[y][x]) {
      return;
    }

    if (gameState.field[y][x].isRevealed) return;

    const newField = [...gameState.field];
    newField[y][x].isFlagged = !newField[y][x].isFlagged;
    
    setGameState(prev => ({ 
      ...prev, 
      field: newField,
      flags: prev.flags + (newField[y][x].isFlagged ? 1 : -1)
    }));
  }, [gameState.field]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      if (gameState.gameStarted && !gameState.gameEnded) {
        setGameState(prev => ({ ...prev, time: prev.time + 1 }));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState.gameStarted, gameState.gameEnded]);

  const startGame = (x: number, y: number) => {
    const field = initializeField();
    const fieldWithMines = placeMines(field, x, y);
    
    // Открываем первую клетку и соседние пустые сразу после создания поля
    const updatedField = revealCellOnField(x, y, fieldWithMines);
    
    setGameState(prev => ({ 
      ...prev, 
      gameStarted: true, 
      field: updatedField 
    }));
  };

  // Открытие клетки на заданном поле (без обращения к gameState)
  const revealCellOnField = (x: number, y: number, field: Cell[][]) => {
    if (!field[y] || !field[y][x]) return field; // добавлена проверка
    if (field[y][x].isRevealed || field[y][x].isFlagged) return field;

    const newField = [...field];
    newField[y][x].isRevealed = true;

    if (newField[y][x].isMine) {
      // Игрок попал на мину - возвращаем поле без изменений
      return newField;
    }

    // Если клетка пустая, рекурсивно открываем соседние
    if (newField[y][x].neighborMines === 0) {
      // Передаем поле С открытой клеткой
      return revealEmptyCells(x, y, newField);
    }

    return newField;
  };

  // 3. restartGame всегда использует актуальный currentDifficulty
  const restartGame = () => {
    const newField = initializeField();
    setGameState({
      time: 0,
      mines: currentDifficulty.mines,
      flags: 0,
      gameStarted: false,
      gameEnded: false,
      won: false,
      gameMode: 'reveal',
      field: newField
    });
    setFieldOffset({ x: 0, y: 0 });
    
    // Останавливаем звук поражения при рестарте игры
    if (webAudioSfx.isReady()) {
      webAudioSfx.stopAll();
    } else {
      mobileAudioManager.stopAudio('titanik');
    }
    
    // Переинициализируем Web Audio после проигрыша
    if (userInteracted) {
      webAudioSfx.markUserInteracted(); // Обновляем состояние
    }
    
    setShowGameOverModal(false);
  };

  const closeGameOverModal = () => {
    console.log('closeGameOverModal вызвана');
    console.log('isWin:', isWin);
    
    // Если игра закончилась поражением, затухаем звук поражения
    if (!isWin) {
      fadeOutTitanikSound();
      
      // Переинициализируем Web Audio после проигрыша
      if (userInteracted) {
        webAudioSfx.markUserInteracted();
      }
    }
    setShowGameOverModal(false);
  };

  // Функция для исправления URL (добавление https:// если нужно)
  const fixUrl = (input: string | undefined | null): string => {
    if (!input) return '';
    
    // Убираем пробелы
    let url = input.trim();
    
    // Добавляем https:// если не указано
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    console.log('Fixed url:', url);
    return url;
  };

  // Обработчик нажатия на кнопку Download после победы
  const handleDownloadClick = () => {
    const buttonLink = props.gameSettings?.winButtonUrl;
    const fileUrl = props.gameSettings?.fileDownloadUrl;
    
    console.log('handleDownloadClick:', { buttonLink, fileUrl });
    
    if (buttonLink && buttonLink.trim()) {
      // Если есть buttonLink - открываем ссылку
      console.log('buttonLink:', buttonLink);
      const fixedUrl = fixUrl(buttonLink);
      window.open(fixedUrl, '_blank');
    } else if (fileUrl && fileUrl.trim()) {
      // Если есть fileUrl - скачиваем файл
      console.log('fileUrl:', fileUrl);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Если все пустое - делаем рестарт игры
      console.log('Все пустое, делаем рестарт');
      restartGame();
      setShowGameOverModal(false);
    }
  };

  const toggleGameMode = () => {
    setGameState(prev => ({ 
      ...prev, 
      gameMode: prev.gameMode === 'reveal' ? 'flag' : 'reveal' 
    }));
  };

  const handleCellClick = React.useCallback((x: number, y: number) => {
    if (gameState.gameEnded) return;
    
    // Звук воспроизводится в onClick обработчике ячейки
    
    if (!gameState.gameStarted) {
      startGame(x, y);
      return;
    }
    
    if (gameState.gameMode === 'flag') {
      toggleFlag(x, y);
    } else {
      revealCell(x, y);
    }
  }, [gameState.gameEnded, gameState.gameStarted, gameState.gameMode, revealCell]);

  // Добавляем обработчик касания для мобильных устройств
  const handleCellTouch = React.useCallback((x: number, y: number) => {
    if (gameState.gameEnded) return;
    
    // Звук уже воспроизведен в onTouchStart, не дублируем
    
    if (!gameState.gameStarted) {
      startGame(x, y);
      return;
    }
    
    if (gameState.gameMode === 'flag') {
      toggleFlag(x, y);
    } else {
      revealCell(x, y);
    }
  }, [gameState.gameEnded, gameState.gameStarted, gameState.gameMode, revealCell]);

  // Защита от двойных кликов на мобильных устройствах
  const lastTouchTimeRef = useRef<number>(0);
  const TOUCH_COOLDOWN = 300; // 300ms задержка между touch событиями
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const MIN_TOUCH_DISTANCE = 10; // Минимальное расстояние для валидного touch
  
  // Определение мобильного устройства
  const isMobileDevice = React.useMemo(() => {
    return 'ontouchstart' in window && window.innerWidth <= 768;
  }, []);
  
  // Функция для безопасной обработки touch событий
  const handleSafeTouch = React.useCallback((x: number, y: number) => {
    if (!isMobileDevice) return;
    
    const now = Date.now();
    if (now - lastTouchTimeRef.current < TOUCH_COOLDOWN) {
      return;
    }
    lastTouchTimeRef.current = now;
    
    // Принудительно инициализируем аудио при первом взаимодействии
    if (!userInteracted) {
      setUserInteracted(true);
      // Попробуем запустить музыку
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {
            // Игнорируем ошибки при первом запуске
          });
        }
      }, 100);
    }
    
    handleCellTouch(x, y);
  }, [isMobileDevice, handleCellTouch, userInteracted]);

  const handleRightClick = React.useCallback((e: React.MouseEvent, x: number, y: number) => {
    e.preventDefault();
    if (!gameState.gameEnded) {
      toggleFlag(x, y);
    }
  }, [gameState.gameEnded, toggleFlag]);

  // Пасхалка: при 10 кликах на заголовок - победа
  const handleTitleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Предотвращаем выделение текста
    if (window.getSelection) {
      window.getSelection()?.removeAllRanges();
    }
    
    const newCount = titleClickCount + 1;
    setTitleClickCount(newCount);
    
    // Анимация масштабирования
    setTitleScaleAnimation(true);
    setTimeout(() => setTitleScaleAnimation(false), 200);
    
    console.log(`🎁 Пасхалка: ${newCount}/10 кликов`);
    
    if (newCount >= 10 && !gameState.gameEnded) {
      console.log('🎉 Пасхалка активирована! Победа!');
      // Воспроизводим звук победы
      playWinSound();
      
      setGameOverMessage(props.gameSettings?.winText || 'Ты победил!');
      setIsWin(true);
      setShowGameOverModal(true);
      
      setGameState(prev => ({ 
        ...prev, 
        gameEnded: true,
        won: true
      }));
    }
  };



  // 5. changeDifficulty только меняет стейт
  // const changeDifficulty = (newDifficulty: 'easy' | 'medium' | 'hard') => {
  //   setDifficulty(newDifficulty);
  //   setGameInstanceKey(prev => prev + 1);
  // };

  // Все useEffect и useCallback, которые зависят от сложности, должны зависеть от props.difficulty и currentDifficulty
  React.useEffect(() => {
    restartGame();
    // eslint-disable-next-line
  }, [props.difficulty, currentDifficulty.mines]);

  // Показываем все мины при проигрыше
  const revealAllMines = () => {
    if (!gameState.gameEnded) return;
    
    const newField = [...gameState.field];
    for (let y = 0; y < currentDifficulty.height; y++) {
      if (!newField[y]) continue; // добавлена проверка
      for (let x = 0; x < currentDifficulty.width; x++) {
        if (!newField[y][x]) continue; // добавлена проверка
        if (newField[y][x].isMine) {
          newField[y][x].isRevealed = true;
        }
      }
    }
    setGameState(prev => ({ ...prev, field: newField }));
  };

  React.useEffect(() => {
    if (gameState.gameEnded) {
      revealAllMines();
    }
  }, [gameState.gameEnded]);

  const wasPlayingBeforeHideRef = useRef(false);
  
  // Защита от множественных вызовов звука
  const lastClickTimeRef = useRef<number>(0);
  const CLICK_COOLDOWN = 30; // 30ms между кликами для всех устройств
  
  // Функции для воспроизведения звуков (единая система)
  const playMouseClickSound = () => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < CLICK_COOLDOWN) {
      return; // Тихий игнор быстрых кликов
    }
    lastClickTimeRef.current = now;
    
    // Проверяем готовность звуков
    if (!sfxReady) {
      console.warn('⚠️ Звуки эффектов еще не готовы');
      return;
    }
    
    // Используем Web Audio API везде (приоритет)
    if (webAudioSfx.isReady()) {
      webAudioSfx.play('click', { volume: 0.3 });
    } else {
      // Фолбек на MobileAudioManager
      mobileAudioManager.playAudio('click', { immediate: true, volume: 0.3 }).catch(error => {
        console.warn('Ошибка воспроизведения звука клика (фолбек):', error);
      });
    }
  };
  
  const playTitanikSound = () => {
    if (webAudioSfx.isReady()) {
      webAudioSfx.play('titanik', { volume: 0.4 });
    } else {
      mobileAudioManager.playAudio('titanik', { volume: 0.4 }).catch(error => {
        console.warn('Ошибка воспроизведения звука титаника (фолбек):', error);
      });
    }
  };
  
  const playWinSound = () => {
    if (webAudioSfx.isReady()) {
      webAudioSfx.play('win', { volume: 0.5 });
    } else {
      mobileAudioManager.playAudio('win', { volume: 0.5 }).catch(error => {
        console.warn('Ошибка воспроизведения звука победы (фолбек):', error);
      });
    }
  };
  
  const playLooseSound = () => {
    if (webAudioSfx.isReady()) {
      webAudioSfx.play('loose', { volume: 0.5 });
    } else {
      mobileAudioManager.playAudio('loose', { volume: 0.5 }).catch(error => {
        console.warn('Ошибка воспроизведения звука проигрыша (фолбек):', error);
      });
    }
  };
  
  const fadeOutTitanikSound = () => {
    // Web Audio буферы одноразовые, используем stopAll для мгновенной остановки
    if (webAudioSfx.isReady()) {
      webAudioSfx.stopAll();
    } else {
      mobileAudioManager.fadeAudio('titanik', 0, 500);
    }
  };

  // Упрощенная функция запуска музыки (как в Capybara)
  const startMusic = () => {
    if (!userInteracted || !audioReady) {
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ Музыка не запущена: userInteracted =', userInteracted, 'audioReady =', audioReady);
      }
      return;
    }
    
    // Не запускаем, если музыка уже играет
    if (isMusicPlaying) {
      return;
    }
    
    // Создаем аудио элемент, если его нет (как в Capybara)
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.src = backgroundMusic;
      audioRef.current.loop = true;
      audioRef.current.volume = 0.4;
      audioRef.current.setAttribute('playsinline', 'true');
      audioRef.current.setAttribute('webkit-playsinline', 'true');
      audioRef.current.setAttribute('x-webkit-airplay', 'allow');
    }
    
    if (audioRef.current) {
      try {
        // Для Safari добавляем специальные атрибуты
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                        /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (isSafari) {
          audioRef.current.setAttribute('playsinline', 'true');
          audioRef.current.setAttribute('webkit-playsinline', 'true');
          audioRef.current.setAttribute('x-webkit-airplay', 'allow');
        }
        
        // Устанавливаем источник (как в Capybara - напрямую из URL)
        audioRef.current.src = backgroundMusic;
        audioRef.current.volume = 0.4;
        audioRef.current.loop = true;
        
        audioRef.current.play()
          .then(() => {
            console.log('✅ Музыка успешно запущена');
            setIsMusicPlaying(true);
          })
          .catch((error) => {
            console.warn('❌ Ошибка при запуске музыки:', error);
            // Попробуем еще раз через небольшую задержку
            setTimeout(() => {
              if (audioRef.current) {
                audioRef.current.play().catch(e => {
                  console.warn('❌ Повторная ошибка при запуске музыки:', e);
                });
              }
            }, 100);
          });
      } catch (error) {
        console.warn('❌ Ошибка при попытке запустить музыку:', error);
      }
    }
  };

  const pauseMusic = () => {
    if (process.env.NODE_ENV === 'development') {
      // console.log('🎵 pauseMusic вызвана:', { hasAudioRef: !!audioRef.current, isPaused: audioRef.current?.paused });
    }
    
    if (audioRef.current && !audioRef.current.paused) {
      try {
        audioRef.current.pause();
      } catch (error) {
        console.warn('Ошибка при постановке музыки на паузу:', error);
      }
    }
  };

  const resumeMusic = () => {
    if (process.env.NODE_ENV === 'development') {
      // console.log('🎵 resumeMusic вызвана:', { userInteracted, audioReady, hasAudioRef: !!audioRef.current, isMusicPlaying });
    }
    
    if (!userInteracted || !audioReady) {
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ Музыка не возобновлена: userInteracted =', userInteracted, 'audioReady =', audioReady);
      }
      return;
    }
    
    // Не возобновляем, если музыка уже играет
    if (isMusicPlaying) {
      if (process.env.NODE_ENV === 'development') {
        // console.log('🎵 Музыка уже играет, пропускаем возобновление');
      }
      return;
    }
    
    if (audioRef.current && audioRef.current.paused) {
      try {
        // Для Safari добавляем специальные атрибуты
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                        /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (isSafari) {
          audioRef.current.setAttribute('playsinline', 'true');
          audioRef.current.setAttribute('webkit-playsinline', 'true');
          audioRef.current.setAttribute('x-webkit-airplay', 'allow');
        }
        
        audioRef.current.play()
          .then(() => {
            setIsMusicPlaying(true);
          })
          .catch((error) => {
            console.error('❌ Ошибка при возобновлении музыки:', error);
            // Попробуем еще раз через небольшую задержку
            setTimeout(() => {
              if (audioRef.current) {
                audioRef.current.play().catch(e => {
                  console.warn('❌ Повторная ошибка при возобновлении музыки:', e);
                });
              }
            }, 100);
          });
      } catch (error) {
        console.warn('❌ Ошибка при попытке возобновить музыку:', error);
      }
    }
  };



  // Автоматический запуск музыки при выполнении всех условий (как в Capybara)
  React.useEffect(() => {
    if (userInteracted && audioReady && !isMusicPlaying) {
      startMusic();
    }
  }, [userInteracted, audioReady]); // Убрали preloadedAudio - используем URL напрямую

  // Предзагрузка всех звуков (единая система)
  React.useEffect(() => {
    const initializeAudio = async () => {
      try {
        // Используем Web Audio API везде (приоритет)
        await Promise.all([
          webAudioSfx.preload('click', mouseClickSound, 0.3),
          webAudioSfx.preload('titanik', titanikSound, 0.4),
          webAudioSfx.preload('win', winSound, 0.5),
          webAudioSfx.preload('loose', looseSound, 0.5)
        ]);
        
        console.log('✅ Устанавливаем audioReady = true (Web Audio API)');
        setAudioReady(true);
      } catch (error) {
        // Фолбек на MobileAudioManager
        try {
          await Promise.all([
            mobileAudioManager.preloadAudio('click', { src: mouseClickSound, volume: 0.3 }),
            mobileAudioManager.preloadAudio('titanik', { src: titanikSound, volume: 0.4 }),
            mobileAudioManager.preloadAudio('win', { src: winSound, volume: 0.5 }),
            mobileAudioManager.preloadAudio('loose', { src: looseSound, volume: 0.5 })
          ]);
        } catch (fallbackError) {
          console.warn('❌ Ошибка фолбека аудио:', fallbackError);
        }
        console.log('✅ Устанавливаем audioReady = true (фолбек)');
        setAudioReady(true);
      }
    };
    
    initializeAudio();
  }, []);

  // Создание и настройка аудио элемента (как в Capybara - напрямую из URL)
  React.useEffect(() => {
    // Создаем аудио элемент программно, если ref не работает
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.src = backgroundMusic;
      audioRef.current.loop = true;
      audioRef.current.volume = 0.4;
      
      // Добавляем атрибуты для Safari
      audioRef.current.setAttribute('playsinline', 'true');
      audioRef.current.setAttribute('webkit-playsinline', 'true');
      audioRef.current.setAttribute('x-webkit-airplay', 'allow');
    } else {
      // Обновляем источник, если он изменился
      audioRef.current.src = backgroundMusic;
    }

    const onPlay = () => {
      setIsMusicPlaying(true);
    };
    const onPause = () => {
      setIsMusicPlaying(false);
    };
    const onEnded = () => {
      setIsMusicPlaying(false);
    };

    if (audioRef.current) {
      audioRef.current.addEventListener('play', onPlay);
      audioRef.current.addEventListener('pause', onPause);
      audioRef.current.addEventListener('ended', onEnded);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('play', onPlay);
        audioRef.current.removeEventListener('pause', onPause);
        audioRef.current.removeEventListener('ended', onEnded);
      }
    };
  }, [backgroundMusic]);

  // Отслеживание изменений audioRef
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔍 audioRef изменился:', audioRef.current);
    }
  }, [audioRef.current]);

  // Авто-пауза музыки при сворачивании окна/переключении вкладки
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        wasPlayingBeforeHideRef.current = !!(audioRef.current && !audioRef.current.paused);
        pauseMusic();
      } else {
        if (wasPlayingBeforeHideRef.current) {
          resumeMusic();
        }
      }
    };

    const handleBlur = () => {
      wasPlayingBeforeHideRef.current = !!(audioRef.current && !audioRef.current.paused);
      pauseMusic();
    };

    const handleFocus = () => {
      if (wasPlayingBeforeHideRef.current) {
        resumeMusic();
      }
    };

    const handlePageHide = () => {
      wasPlayingBeforeHideRef.current = !!(audioRef.current && !audioRef.current.paused);
      pauseMusic();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Обработчик пользовательского взаимодействия для разблокировки аудио
  React.useEffect(() => {
    const handleUserInteraction = () => {
      console.log('👆 handleUserInteraction вызвана, userInteracted =', userInteracted);
      
      if (!userInteracted) {
        console.log('✅ Устанавливаем userInteracted = true');
        setUserInteracted(true);
        webAudioSfx.markUserInteracted(); // Разблокируем Web Audio (приоритет)
        mobileAudioManager.setUserInteracted(); // Фолбек
        
        // Пробуем воспроизвести короткий тихий звук для разблокировки
        if (webAudioSfx.isReady()) {
          webAudioSfx.play('click', { volume: 0.01 });
        } else {
          mobileAudioManager.playAudio('click', { volume: 0.01, immediate: true }).catch(() => {});
        }
      }
    };

    // Слушаем различные события взаимодействия с приоритетом на touchstart для мобильных
    const events = ['touchstart', 'click', 'keydown', 'mousedown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [userInteracted]);

  // Скрывать скролл только когда компонент смонтирован
  React.useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlHeight = document.documentElement.style.height;
    const prevBodyPosition = document.body.style.position;
    const prevHtmlPosition = document.documentElement.style.position;
    
    // Устанавливаем стили для предотвращения скролла в Safari
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.width = '100%';
    
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
      document.body.style.position = prevBodyPosition;
      document.body.style.width = '';
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.height = prevHtmlHeight;
      document.documentElement.style.position = prevHtmlPosition;
      document.documentElement.style.width = '';
    };
  }, []);

  // Размеры контейнера (9:16)
  const ASPECT_RATIO = 9.5 / 16;
  const BASE_WIDTH = 1080;
  const BASE_HEIGHT = 1920;
  const [containerSize, setContainerSize] = useState({ width: BASE_WIDTH, height: BASE_HEIGHT });

  // Центрирование и масштабирование контейнера
  React.useEffect(() => {
    const updateSize = () => {
      // Используем более надежный способ получения размеров для Safari
      const windowWidth = window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth;
      const windowHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
      
      // Добавляем небольшой отступ для Safari
      const safeHeight = windowHeight * 0.95; // 95% от высоты окна
      
      let width = BASE_WIDTH;
      let height = BASE_HEIGHT;
      
      if (windowWidth / safeHeight > ASPECT_RATIO) {
        // Окно шире — ограничиваем по высоте
        height = Math.min(safeHeight, BASE_HEIGHT);
        width = Math.round(height * ASPECT_RATIO);
      } else {
        // Окно уже — ограничиваем по ширине
        width = Math.min(windowWidth * 0.95, BASE_WIDTH); // 95% от ширины окна
        height = Math.round(width / ASPECT_RATIO);
      }
      
      setContainerSize({ width, height });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  }, []);

  // cellSize теперь зависит только от размеров контейнера и сложности
  React.useEffect(() => {
    const padding =10;
    const availableWidth = containerSize.width - padding * 2;
    const reservedTopPx = Math.max(containerSize.height * 0.2, 120); // заголовок
    const reservedBottomPx = panelPx + 39; // панель + отступы
    const availableHeight = containerSize.height - reservedTopPx - reservedBottomPx;
    const maxWidth = availableWidth / currentDifficulty.width;
    const maxHeight = availableHeight / currentDifficulty.height;
    const newCellSize = Math.min(maxWidth, maxHeight);
    setCellSize(newCellSize);
  }, [containerSize, currentDifficulty.width, currentDifficulty.height]);

  // Состояние для готовности заголовка
  const [titleReady, setTitleReady] = useState(false);

  // Вычисление размера шрифта для заголовка только после загрузки ассетов и появления контейнера
  React.useEffect(() => {
    if (
      props.gameSettings?.customTitle &&
      containerRef.current &&
      assetsLoaded
    ) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      // Учитываем отступы заголовка (40px = 20px с каждой стороны)
      const headerWidth = containerWidth - 65;
      const maxHeaderHeight = containerSize.height * (isNarrow ? 0.12 : 0.18);
      const newFontSize = calculateTitleFontSize(
        props.gameSettings.customTitle,
        maxHeaderHeight,
        headerWidth,
        maxHeaderHeight
      );
      setTitleFontSize(newFontSize);
      setTitleReady(true); // теперь можно показывать заголовок
    } else {
      setTitleReady(false);
    }
    // eslint-disable-next-line
  }, [props.gameSettings?.customTitle, containerSize.height, containerSize.width, assetsLoaded]);

  // Флаг адаптивности (нужен раньше, чем расчёт panelPx)
  const isNarrow = containerSize.width <= 480;

  // Фиксируем размеры заголовка и панели
  const headerPx = 120;
  // Динамическая панель: десктоп ~7.5% высоты, мобилка ~6.0–6.5%; разные минимумы
  const panelPx = Math.round(
    Math.max(
      isNarrow ? 56 : 90,
      Math.min(isNarrow ? 90 : 150, containerSize.height * (isNarrow ? 0.06 : 0.075))
    )
  );

  // UI-размеры
  const modalWidthPx = Math.min(containerSize.width * 0.9, 720);
  const modalImageSizePx = Math.min(containerSize.width * 0.8, 600);
  const modalButtonSizePx = Math.max(56, Math.min(96, containerSize.width * 0.15));
  const modalTitleFontSizePx = Math.max(18, Math.min(36, containerSize.width * 0.05));
  const modalMaxHeightPx = Math.min(containerSize.height * 0.9, 900);
  // Элементы панели масштабируем от её высоты
  const ctrlBtnSize = Math.max(32, Math.min(isNarrow ? 48 : 80, panelPx - 20));
  const ctrlIconSize = Math.round(ctrlBtnSize * 0.55);
  const ctrlFontSize = Math.round(ctrlBtnSize * 0.42);
  const ctrlSmileFontSize = Math.round(ctrlBtnSize * 0.46);
  const displayFontSize = Math.round(ctrlBtnSize * 0.42);
  const displayMinWidth = Math.round(ctrlBtnSize * (isNarrow ? 1.15 : 1.3));
  const panelGap = Math.max(4, Math.min(10, Math.round(ctrlBtnSize * 0.22)));
  const panelPadding = Math.max(4, Math.min(10, Math.round(ctrlBtnSize * 0.18)));
  const colGap = Math.max(2, Math.min(4, Math.round(ctrlBtnSize * 0.1)));
  const sevenSegHeight = Math.max(14, Math.round(ctrlBtnSize * (isNarrow ? 0.5 : 0.6)));

  // Толщина рамок поля и ячеек — немного тоньше
  const cellBorderPx = Math.max(1, Math.min(2, Math.round(cellSize * 0.05)));

  // Семисегментный дисплей для цифр (Win95-like)
  const SevenSegDigit: React.FC<{ digit: string; height: number; onColor?: string; offColor?: string }>
    = ({ digit, height, onColor = '#FF0000', offColor = '#320000' }) => {
    const W = 60; const H = 100; const t = 14; const m = 6;
    const active: Record<string, boolean> = { a: false, b: false, c: false, d: false, e: false, f: false, g: false };
    const map: Record<string, Array<keyof typeof active>> = {
      '0': ['a','b','c','d','e','f'],
      // Для "1" подсветим сегмент g чуть-чуть (тонкой линией), чтобы
      // визуально цифра казалась выше. Эмулируем это, делая сами сегменты b/c чуть длиннее.
      '1': ['b','c'],
      '2': ['a','b','g','e','d'],
      '3': ['a','b','c','d','g'],
      '4': ['f','g','b','c'],
      '5': ['a','f','g','c','d'],
      '6': ['a','f','g','e','c','d'],
      '7': ['a','b','c'],
      '8': ['a','b','c','d','e','f','g'],
      '9': ['a','b','c','d','f','g'],
      '-': ['g']
    };
    (map[digit] || []).forEach(s => active[s] = true);
    const scale = (val: number) => (val * height) / H;
    const width = scale(W);
    const thick = scale(t);
    const mm = scale(m);
    const seg = (x: number, y: number, w: number, h: number) => ({ x: scale(x), y: scale(y), w: scale(w), h: scale(h) });
    const a = seg(m, m, W - 2*m, t);
    const d = seg(m, H - m - t, W - 2*m, t);
    const g = seg(m, H/2 - t/2, W - 2*m, t);
    const b = seg(W - m - t, m + Math.max(t * 0.6, 8), t, H/2 - m - t*1.1);
    const c = seg(W - m - t, H/2 + Math.max(t * 0.2, 4), t, H - (H/2 + Math.max(t * 0.2, 4)) - m - t*0.9);
    const f = seg(m, m + t, t, H/2 - m - t*1.5);
    const e = seg(m, H/2 + t/2, t, H - (H/2 + t/2) - m - t);
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
        {[{key:'a',rect:a},{key:'b',rect:b},{key:'c',rect:c},{key:'d',rect:d},{key:'e',rect:e},{key:'f',rect:f},{key:'g',rect:g}]
          .map(({key,rect}) => (
            <rect key={key} x={rect.x} y={rect.y} width={rect.w} height={rect.h} rx={scale(3)} ry={scale(3)}
              fill={active[key as keyof typeof active] ? onColor : offColor} />
          ))}
      </svg>
    );
  };

  const SevenSegDisplay: React.FC<{ value: number | string; digits?: number; height: number }>
    = ({ value, digits = 3, height }) => {
    const str = String(value).padStart(digits, '0').slice(-digits);
    const digitHeight = height;
    const gap = Math.max(2, Math.round(height * 0.12));
    const digitWidth = Math.round((60/100) * digitHeight); // пропорции SVG
    const totalWidth = digits * digitWidth + (digits - 1) * gap;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: `${gap}px`, width: `${totalWidth}px`, height: `${digitHeight}px` }}>
        {str.split('').map((ch, idx) => (
          <SevenSegDigit key={idx} digit={ch} height={digitHeight} />
        ))}
      </div>
    );
  };

  // Найти или создать контейнер для портала
  const [panelRoot, setPanelRoot] = useState<HTMLElement | null>(null);
  React.useEffect(() => {
    let el = document.getElementById('miner-control-panel-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'miner-control-panel-root';
      document.body.appendChild(el);
    }
    setPanelRoot(el);
    return () => {
      // Не удаляем div, чтобы не ломать другие рендеры
    };
  }, []);

  // Удаляю прежний return с лоадером
  // Вместо этого рендерю контейнер всегда, а внутри — лоадер или основной контент
  // Удаляю внутренние useState для showIntroModal, isPlaying
  // Использую props.showIntroModal, props.isPlaying, props.handleGiftClick, props.handleLottieComplete, props.lottieRef
  // Проверяем, что данные из JSON загружены и ассеты готовы
  // Игра должна показываться только после полной загрузки данных
  // Проверяем что gameSettings существует и содержит хотя бы основные поля
  const isDataReady = props.gameSettings && 
    typeof props.gameSettings === 'object' &&
    (props.gameSettings.customTitle !== undefined ||
     props.gameSettings.introText !== undefined ||
     props.gameSettings.moneyText !== undefined ||
     props.gameSettings.winText !== undefined ||
     props.gameSettings.loseText !== undefined);
  
  // Если данные или ассеты не готовы, не рендерим компонент вообще
  // MinerGameWrapper сам покажет экран загрузки
  if (!isDataReady || !assetsLoaded) {
    return null;
  }

  // В блоке с модалкой:
  if (props.showIntroModal && assetsLoaded && titleReady && isDataReady) {
    return (
      <div style={{
        position: 'fixed',
        zIndex: 9999,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        background: '#fff',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        opacity: props.modalOpacity,
        transition: 'opacity 0.3s ease-out',
      }}>
        {/* Заголовок как в игре */}
        {props.gameSettings?.moneyText && (
                      <div
              className="miner-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 15px',
                marginTop: '14px',
                marginBottom: '0px',
                minHeight: '48px',
                maxHeight: '120px',
                height: 'auto',
                overflow: 'hidden',
                width: 'calc(100% - 30px)',
                marginLeft: '15px',
                marginRight: '15px',
                boxSizing: 'border-box'
              }}
            >
                            <h1
                  style={{
                    margin: 0,
                    fontWeight: '600',
                    color: '#000000',
                    fontSize: `${Math.max(20, Math.min(36, (containerSize.width) * 0.03))}px`,
                    lineHeight: '1.1',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    textAlign: 'center',
                    maxWidth: '100%',
                    width: '100%',
                    height: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: '"Raleway"',
                    padding: '0 10px',
                    boxSizing: 'border-box'
                  }}
                >
              {props.gameSettings.moneyText}
            </h1>
          </div>
        )}
        <div
          style={{
            cursor: props.isPlaying ? 'default' : 'pointer',
            width: `${Math.min(containerSize.width * 0.6, 420)}px`,
            height: `${Math.min(containerSize.width * 0.6, 420)}px`,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
            MozUserSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
          onClick={!props.isPlaying ? props.handleGiftClick : undefined}
        >
          <Lottie
            lottieRef={props.lottieRef}
            animationData={giftAnim}
            loop={false}
            autoplay={false}
            style={{ width: '100%', height: '100%' }}
            onComplete={props.handleLottieComplete}
          />
        </div>
      </div>
    );
  }
  
  return (
    <div
      key={gameInstanceKey}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '100vh',
        background: '#008080',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflow: 'hidden',
        overscrollBehavior: 'none',
        touchAction: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        opacity: props.gameOpacity,
        transition: 'opacity 0.5s ease-in',
      }}
    >
      {/* Скрытый аудио элемент для фоновой музыки */}
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
      />
      <div
        ref={containerRef}
        style={{
          width: `${containerSize.width}px`,
          height: `${containerSize.height}px`,
            backgroundColor: '#C0C0C0',
            boxShadow: '0 0 0 2px #fff inset, 0 0 0 5px #808080 inset, 0 0 32px rgba(0,0,0,0.25)',
            borderRadius: '8px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        {/* Заголовок окна в стиле Windows 95 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '42px',
            background: 'linear-gradient(#000080, #000050)',
            color: '#fff',
            padding: '0 8px',
            boxSizing: 'border-box',
            borderBottom: '2px solid #808080'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 22, height: 22, background: '#C0C0C0', border: '2px solid #fff', borderRight: '2px solid #808080', borderBottom: '2px solid #808080' }} />
            <div style={{ fontWeight: 700, fontFamily: 'Tahoma, "MS Sans Serif", Arial, sans-serif', letterSpacing: 0.3 }}>
              Minesweeper
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {['—','□','×'].map((s) => (
              <div key={s} style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#C0C0C0',
                border: '2px solid #FFFFFF',
                borderRight: '2px solid #808080',
                borderBottom: '2px solid #808080',
                fontWeight: 700,
                color: '#000'
              }}>
                {s}
              </div>
            ))}
          </div>
        </div>
        {/* Заголовок */}
        {props.gameSettings?.customTitle && titleReady && (
                      <div
              className="miner-header"
              onClick={handleTitleClick}
              onDoubleClick={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 0px',
              marginTop: '10px',
                marginBottom: '0px',
                backgroundColor: 'transparent',
                minHeight: '48px',
                maxHeight: `${containerSize.height * 0.2}px`,
                height: 'auto',
                overflow: 'hidden',
              width: 'calc(100% - 20px)',
              marginLeft: '10px',
              marginRight: '10px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                transform: titleScaleAnimation ? 'scale(0.95)' : 'scale(1)'
                }}
              >
                <h1
                  style={{
                    margin: 0,
                    fontWeight: '600',
                    color: '#000000',
                    fontSize: `${titleFontSize}px`,
                    lineHeight: '1.1',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    textAlign: 'center',
                    maxWidth: '100%',
                    width: '100%',
                    height: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                fontFamily: 'Tahoma, "MS Sans Serif", Arial, sans-serif',
                    padding: '0 10px',
                    boxSizing: 'border-box'
                  }}
                >
                  {props.gameSettings.customTitle}
                </h1>
              </div>
            )}
            {/* Игровое поле */}
            <div
              style={{
                width: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                background: 'none',
                overflow: 'hidden',
                paddingTop: '14px',
              }}
            >
              <div
                ref={fieldRef}
                className="miner-game-field"
                onTouchMove={(e) => e.preventDefault()}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${currentDifficulty.width}, ${cellSize}px)`,
                  gridTemplateRows: `repeat(${currentDifficulty.height}, ${cellSize}px)`,
                  gap: `${Math.max(1, Math.round(cellSize * 0.025))}px`,
                  backgroundColor: '#9c9c9c',
                  border: `${Math.max(2, Math.round(cellSize * 0.06))}px solid #808080`,
                  padding: '0px',
                  transform: `translate(${fieldOffset.x}px, ${fieldOffset.y}px)`,
                  cursor: 'default',
                  userSelect: 'none',
                  width: 'fit-content',
                  height: 'fit-content',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  margin: '0 auto',
                  touchAction: 'none',
                }}
              >
                {Array.from({ length: currentDifficulty.width * currentDifficulty.height }, (_, i) => {
                  const x = i % currentDifficulty.width;
                  const y = Math.floor(i / currentDifficulty.width);
                  const cell = gameState.field[y]?.[x] || { isMine: false, isRevealed: false, isFlagged: false, neighborMines: 0 };
                  let cellContent = '';
                  let cellColor = '#C0C0C0';
                  let cellImage = null;
                  if (cell.isFlagged) {
                    cellImage = flagSvg;
                  } else if (cell.isRevealed) {
                    if (cell.isMine) {
                      cellImage = bombSvg;
                      cellColor = '#FF0000';
                    } else if (cell.neighborMines > 0) {
                      cellContent = cell.neighborMines.toString();
                      // Цвета как в оригинальном Minesweeper (Windows): 1-синий, 2-зелёный, 3-красный, 4-тёмно-синий, 5-бордовый, 6-циан, 7-чёрный, 8-серый
                      const colors = ['', '#0000FF', '#008200', '#FF0000', '#000084', '#840000', '#008284', '#000000', '#757575'];
                      cellColor = colors[cell.neighborMines] || '#000000';
                    }
                  }
                  return (
                    <div
                      key={i}
                      className="miner-cell"
                      onClick={(e) => {
                        // На мобильных устройствах игнорируем onClick, используем только touch события
                        if (isMobileDevice) {
                          return;
                        }
                        e.stopPropagation();
                        
                        // Принудительно инициализируем аудио при первом взаимодействии
                        if (!userInteracted) {
                          setUserInteracted(true);
                          // Попробуем запустить музыку
                          setTimeout(() => {
                            if (audioRef.current) {
                              audioRef.current.play().catch(() => {
                                // Игнорируем ошибки при первом запуске
                              });
                            }
                          }, 100);
                        }
                        
                        // Воспроизводим звук клика мыши
                        playMouseClickSound();
                        handleCellClick(x, y);
                      }}
                      onTouchStart={(e) => {
                        // Только для мобильных устройств
                        if (isMobileDevice) {
                          e.stopPropagation();
                          // Запоминаем позицию начала касания
                          const touch = e.touches[0];
                          touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
                          // Немедленно воспроизводим звук при касании
                          playMouseClickSound();
                        }
                      }}
                      onTouchEnd={(e) => {
                        // Только для мобильных устройств
                        if (isMobileDevice) {
                          e.stopPropagation();
                          
                          // Проверяем, что touch не слишком короткий (защита от случайных касаний)
                          if (touchStartPosRef.current) {
                            const touch = e.changedTouches[0];
                            const distance = Math.sqrt(
                              Math.pow(touch.clientX - touchStartPosRef.current.x, 2) +
                              Math.pow(touch.clientY - touchStartPosRef.current.y, 2)
                            );
                            
                            if (distance <= MIN_TOUCH_DISTANCE) {
                              handleSafeTouch(x, y);
                            }
                          }
                          
                          touchStartPosRef.current = null;
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRightClick(e, x, y);
                      }}
                      style={{
                        width: `${cellSize}px`,
                        height: `${cellSize}px`,
                        backgroundColor: cell.isRevealed ? '#E0E0E0' : cellColor,
                        border: `${cellBorderPx + 1}px solid #FFFFFF`,
                        borderRight: `${cellBorderPx + 1}px solid #808080`,
                        borderBottom: `${cellBorderPx + 1}px solid #808080`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: `${Math.max(cellSize * 0.52, 12)}px`,
                        fontFamily: 'Tahoma, "MS Sans Serif", Arial, sans-serif',
                        fontWeight: '900',
                        userSelect: 'none',
                        color: cellColor === '#C0C0C0' ? '#000000' : cellColor,
                        pointerEvents: 'auto',
                        boxSizing: 'border-box',
                      }}
                    >
                      {cellImage ? (
                        <img 
                          src={cellImage} 
                          alt={cell.isFlagged ? 'flag' : 'bomb'}
                          style={{
                            width: `${cellSize * 0.6}px`,
                            height: `${cellSize * 0.6}px`,
                            filter: cell.isMine ? 'brightness(0.8) saturate(1.2)' : 'none'
                          }}
                        />
                      ) : (
                        cellContent
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Панель управления */}
            <div
              style={{
            backgroundColor: '#C0C0C0',
            borderTop: '3px solid #808080',
                height: `${panelPx}px`,
                maxHeight: `${panelPx}px`,
                minHeight: `${panelPx}px`,
                boxSizing: 'border-box',
                width: '100%',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                pointerEvents: 'all',
                display: 'flex',
            justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
              justifyContent: 'center',
                  alignItems: 'center',
                  gap: `${panelGap}px`,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
              padding: `${panelPadding}px`,
                }}
              >
                {/* Кнопка флажка */}
                <button 
                  onClick={toggleGameMode}
                  style={{
                width: `${ctrlBtnSize}px`,
                height: `${ctrlBtnSize}px`,
                fontSize: `${ctrlFontSize}px`,
                    border: '3px solid #FFFFFF',
                    borderRight: '3px solid #808080',
                    borderBottom: '3px solid #808080',
                    backgroundColor: gameState.gameMode === 'flag' ? '#FFFF00' : '#C0C0C0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <img 
                    src={flagSvg} 
                    alt="flag"
                    style={{
                  width: `${ctrlIconSize}px`,
                  height: `${ctrlIconSize}px`
                    }}
                  />
                </button>
                
                {/* Счетчик мин – семисегментный дисплей */}
                <div style={{
                  backgroundColor: '#000',
                  padding: `${Math.max(4, Math.round(ctrlBtnSize * 0.14))}px ${Math.max(6, Math.round(ctrlBtnSize * 0.2))}px`,
                  border: '3px solid #808080',
                  borderRadius: '4px',
                  minWidth: `${displayMinWidth}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <SevenSegDisplay value={(gameState.mines - gameState.flags)} digits={3} height={sevenSegHeight} />
                </div>
                
                {/* Кнопка смайлика */}
                <button 
                  onClick={() => restartGame()}
                  style={{
                width: `${ctrlBtnSize}px`,
                height: `${ctrlBtnSize}px`,
                fontSize: `${ctrlSmileFontSize}px`,
                    border: '3px solid #FFFFFF',
                    borderRight: '3px solid #808080',
                    borderBottom: '3px solid #808080',
                    backgroundColor: '#C0C0C0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  {gameState.gameEnded ? (
                    gameState.won ? (
                      <img 
                        src={smileSvg} 
                        alt="win"
                        style={{
                      width: `${ctrlIconSize}px`,
                      height: `${ctrlIconSize}px`,
                          filter: 'brightness(1.2) saturate(1.2)'
                        }}
                      />
                    ) : (
                      <img 
                        src={smileSvg} 
                        alt="lose"
                        style={{
                      width: `${ctrlIconSize}px`,
                      height: `${ctrlIconSize}px`,
                          filter: 'brightness(0.7) saturate(0.8) grayscale(0.3)'
                        }}
                      />
                    )
                  ) : (
                    <img 
                      src={smileSvg} 
                      alt="smile"
                      style={{
                    width: `${ctrlIconSize}px`,
                    height: `${ctrlIconSize}px`
                      }}
                    />
                  )}
                </button>
                
                {/* Таймер – семисегментный дисплей */}
                <div style={{
                  backgroundColor: '#000',
                  padding: `${Math.max(4, Math.round(ctrlBtnSize * 0.14))}px ${Math.max(6, Math.round(ctrlBtnSize * 0.2))}px`,
                  border: '3px solid #808080',
                  borderRadius: '4px',
                  minWidth: `${displayMinWidth}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <SevenSegDisplay value={gameState.time} digits={3} height={sevenSegHeight} />
                </div>
                
                {/* Вертикальная группа кнопок */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
              gap: `${colGap}px`,
                  flexShrink: 0
                }}>
                  {/* Кнопка настроек */}
                  <button 
                    onClick={() => {
                      const newDifficulty = props.difficulty === 'easy' ? 'medium' : props.difficulty === 'medium' ? 'hard' : 'easy';
                      props.onChangeDifficulty && props.onChangeDifficulty(newDifficulty);
                    }}
                    style={{
                  width: `${ctrlBtnSize}px`,
                  height: `${ctrlBtnSize}px`,
                  fontSize: `${ctrlFontSize}px`,
                      border: '3px solid #FFFFFF',
                      borderRight: '3px solid #808080',
                      borderBottom: '3px solid #808080',
                      backgroundColor: '#C0C0C0',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <img 
                      src={settingsSvg} 
                      alt="settings"
                      style={{
                    width: `${ctrlIconSize}px`,
                    height: `${ctrlIconSize}px`
                      }}
                    />
                  </button>
                  
                  {/* Кнопка управления музыкой */}
                  {/* УДАЛЕНО: Кнопка управления музыкой */}
                </div>
              </div>
            </div>
            {/* Модальное окно окончания игры */}
            {showGameOverModal && (
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100vw',
                  height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000,
                  padding: '10px',
                  boxSizing: 'border-box',
                  overflow: 'auto'
                }}>
                <div 
                  style={{
                    backgroundColor: '#C0C0C0',
                    border: '3px solid #808080',
                    borderRadius: '8px',
                    padding: '15px',
                    textAlign: 'center',
                    maxWidth: `calc(100% - 20px)`,
                    width: `${Math.min(modalWidthPx, containerSize.width - 40)}px`,
                    maxHeight: `calc(100vh - 40px)`,
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    boxSizing: 'border-box',
                    margin: 'auto',
                    flexShrink: 0
                  }}>
                  {/* Аватар пользователя и картинка */}
                  <div style={{
                    width: `${modalImageSizePx}px`,
                    height: `${modalImageSizePx}px`,
                    borderRadius: '8px',
                    backgroundColor: '#808080',
                    margin: '0 0 15px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${Math.max(32, Math.min(64, containerSize.width * 0.06))}px`,
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    {!isWin && preloadedImages.start && (
                      <img src={preloadedImages.start} alt="Проигрыш" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px', objectFit: 'contain' }} />
                    )}
                    {isWin && preloadedImages.end && (
                      <img src={preloadedImages.end} alt="Победа" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px', objectFit: 'contain' }} />
                    )}
                  </div>
                  {/* Сообщение */}
                  <h2 style={{
                    margin: '0 0 20px 0',
                    color: isWin ? '#6a329f' : '#FF0000',
                    fontSize: `${modalTitleFontSizePx}px`,
                    fontWeight: '600',
                    fontFamily: 'Tahoma, "MS Sans Serif", Arial, sans-serif',
                    textAlign: 'center',
                    wordWrap: 'break-word'
                  }}>
                    {gameOverMessage}
                  </h2>
                  {/* Кнопки */}
                  <div style={{
                    display: 'flex',
                    gap: '20px',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginTop: '10px'
                  }}>
                    {/* После проигрыша — только рестарт */}
                    {!isWin && (
                      <button
                        onClick={() => restartGame()}
                        style={{
                          width: `${modalButtonSizePx}px`,
                          height: `${modalButtonSizePx}px`,
                          aspectRatio: '1 / 1',
                          border: 'none',
                          background: 'none',
                          borderRadius: '50%',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          margin: '0 auto',
                          cursor: 'pointer',
                          boxShadow: 'none',
                        }}
                        title="Restart"
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            border: '3px solid #fce5cd',
                            borderRadius: '50%',
                            backgroundColor: '#C0C0C0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: `${Math.max(28, Math.min(40, containerSize.width * 0.04))}px`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            boxSizing: 'border-box',
                          }}
                        >
                          <img 
                            src={smileSvg} 
                            alt="restart"
                            style={{
                              width: '56%',
                              height: '56%'
                            }}
                          />
                        </div>
                      </button>
                    )}
                    {/* После победы — только кнопка Download */}
                    {isWin && (
                      <button
                        onClick={handleDownloadClick}
                        style={{
                          width: `${modalButtonSizePx}px`,
                          height: `${modalButtonSizePx}px`,
                          aspectRatio: '1 / 1',
                          border: 'none',
                          background: 'none',
                          borderRadius: '50%',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          margin: '0 auto',
                          cursor: 'pointer',
                          boxShadow: 'none',
                        }}
                        title="Download"
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            border: '2px solid #eeeeee',
                            borderRadius: '30%',
                            backgroundColor: '#C0C0C0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: `${Math.max(28, Math.min(40, containerSize.width * 0.04))}px`,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            boxSizing: 'border-box',
                          }}
                        >
                          <svg
                            width="60%"
                            height="60%"
                            viewBox="0 0 24 24"
                            fill="#e53935"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M11 14v8h-4a3 3 0 0 1 -3 -3v-4a1 1 0 0 1 1 -1h6zm8 0a1 1 0 0 1 1 1v4a3 3 0 0 1 -3 3h-4v-8h6zm-2.5 -12a3.5 3.5 0 0 1 3.163 5h.337a2 2 0 0 1 2 2v1a2 2 0 0 1 -2 2h-7v-5h-2v5h-7a2 2 0 0 1 -2 -2v-1a2 2 0 0 1 2 -2h.337a3.486 3.486 0 0 1 -.337 -1.5c0 -1.933 1.567 -3.5 3.483 -3.5c1.755 -.03 3.312 1.092 4.381 2.934l.136 .243c1.033 -1.914 2.56 -3.114 4.291 -3.175l.209 -.002zm-9 2a1.5 1.5 0 0 0 0 3h3.143c-.741 -1.905 -1.949 -3.02 -3.143 -3zm8.983 0c-1.18 -.02 -2.385 1.096 -3.126 3h3.143a1.5 1.5 0 1 0 -.017 -3z"/>
                          </svg>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
       
      </div>
    </div>
  );
};

// Основной компонент
const MinerGame = ({ gameKey, gameSettings }: { gameKey: any; gameSettings?: any }) => {
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [instanceKey, setInstanceKey] = useState(0);

  const changeDifficulty = (newDifficulty: 'easy' | 'medium' | 'hard') => {
    setDifficulty(newDifficulty);
    setInstanceKey(prev => prev + 1);
  };

  // В MinerGame:
  const [showIntroModal, setShowIntroModal] = useState(false); // Отключаем экран загрузки для Минера
  const [isPlaying, setIsPlaying] = useState(false);
  const lottieRef = useRef<any>(null);

  const handleGiftClick = () => {
    setIsPlaying(true);
    if (lottieRef.current) {
      lottieRef.current.play();
    }
  };
  // Добавляю состояние для плавного исчезновения
  const [modalOpacity, setModalOpacity] = useState(1);
  // В MinerGame добавляю состояние для плавного появления игры:
  const [gameOpacity, setGameOpacity] = useState(1); // начинаем с 1, чтобы игра была сразу видна

  // В handleLottieComplete только показываем игру, музыка запустится автоматически:
  const handleLottieComplete = () => {
    console.log('🎬 handleLottieComplete вызвана');
    // Плавно скрываем модалку
    setModalOpacity(0);
    // Через 300ms полностью убираем модалку и показываем игру
    setTimeout(() => {
      console.log('🎬 Показываем игру');
      setShowIntroModal(false);
      setModalOpacity(1); // сбрасываем для следующего показа
      setGameOpacity(1); // показываем игру
      // Музыка запустится автоматически через useEffect
    }, 300);
  };

  return (
    <MinerGameInstance
      key={instanceKey}
      difficulty={difficulty}
      gameKey={gameKey}
      gameSettings={gameSettings}
      onChangeDifficulty={changeDifficulty}
      showIntroModal={showIntroModal}
      isPlaying={isPlaying}
      handleGiftClick={handleGiftClick}
      handleLottieComplete={handleLottieComplete}
      lottieRef={lottieRef}

      modalOpacity={modalOpacity}
      gameOpacity={gameOpacity}
    />
  );
};

export default MinerGame; 